<template>
  <v-modal
    class="checkout-modal"
    @initialOpen="handleModalOpen"
    @close="handleModalClose"
    :xShort="isLoadingStage"
    :disable-close="stage === STAGES.PAY_IAP && !iapError"
  >
    <template v-slot:before>
      <avatar v-if="!isLoadingStage" :image="app.icon" :altText="app.name" />
    </template>

    <template :slot="STAGES.START">
      <div class="p-5 text-center">
        <v-loader />
      </div>
    </template>

    <template :slot="STAGES.PAY_IAP">
      <div class="p-5 text-center d-flex flex-column align-items-center">
        <v-loader v-if="!iapError" />
        <transition name="fade-out-left" mode="out-in">
          <div v-if="iapIsLoading" class="text-grey-60">
            <transition name="fade-out-left" mode="out-in">
              <p
                v-if="iapProgressStep == 2"
                key="iap-third"
                class="text-grey-60"
              >
                Checking out with Apple
              </p>
              <p
                v-else-if="iapProgressStep == 1"
                key="iap-secondary"
                class="text-grey-60"
              >
                Prompting Checkout...
              </p>
              <p v-else key="iap-initial" class="text-grey-60">
                Loading Apple Checkout
              </p>
            </transition>
          </div>
          <p v-else-if="iapIsValidating" class="text-grey-60">
            Processing Purchase
          </p>
          <div v-else-if="iapError">
            <div v-if="iapError === IAP_ERRORS.INVALID_PRODUCT">
              <v-icon icon="error_outline" size="2.75rem" />
              <h3>Could Not Find Product</h3>
              <p class="text-grey-60">
                If you are the developer, please refer to the App Store Setup
                guide
              </p>
            </div>
          </div>
        </transition>
      </div>
    </template>

    <template :slot="STAGES.ERROR">
      <div class="checkout-modal__content">
        <div class="mt-3">
          <h2 class="mb-2">There was a problem processing your purchase</h2>
          <p class="lead mt-0 mb-4" v-html="error.message" />
          <template v-if="error.reason === ERROR_REASONS.existing_subscription">
            <v-button primary block @click="() => $router.push('#auth')">
              Sign In
            </v-button>
          </template>
          <template v-else>
            <v-button secondary block @click="retry">
              Retry
            </v-button>
          </template>

          <template v-if="isAuthenticated">
            <v-button ghost block class="mt-3" to="/me/account/support">
              Get help
            </v-button>
          </template>
        </div>
      </div>
    </template>

    <template :slot="STAGES.PAY">
      <div class="checkout-modal__content">
        <confirm-purchase :product="product" @error="handlePurchaseError" />
      </div>
    </template>

    <template :slot="STAGES.CALLBACK">
      <payment-element-callback
        @success="handlePurchaseSuccess"
        @error="handlePurchaseError"
      />
    </template>

    <template v-slot:after>
      <v-made-with-movement :allow-fixed="false" />
    </template>
  </v-modal>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"

import { ACTIVITY_TYPES } from "@/store/analytics"

import EventBus, { EVENTS } from "@/lib/event-bus"
import { isAppStore, isIos } from "@/lib/is-platform"
import {
  postMessageToNativeApp,
  setupNativePurchaseEventListeners,
  buildMessage,
  MESSAGE_TYPES,
  IAP_PURCHASE_PROPERTIES
} from "@/lib/app-store-utils"

import {
  STAGES,
  ERROR_REASONS,
  SETUP_INTENT_PRODUCT_ID
} from "@/lib/checkout-utils"

import Avatar from "@/components/Avatar"

const IAP_ERRORS = {
  INVALID_PRODUCT: "invalid_product"
}

export default {
  components: {
    Avatar,
    ConfirmPurchase: () => import("@/components/checkout/ConfirmPurchase"),
    PaymentElementCallback: () =>
      import("@/components/checkout/PaymentElementCallback")
  },

  props: {
    product: { type: Object, required: true }
  },

  data() {
    return {
      paymentMethod: {},
      paymentUser: null,
      error: "",
      stage: STAGES.START,
      isHandlingAuthComplete: false,
      iapIsLoading: false,
      iapIsValidating: false,
      iapError: null,
      iapProgressStep: 0,
      STAGES,
      IAP_ERRORS,
      ERROR_REASONS
    }
  },

  methods: {
    ...mapActions("auth", ["fetchUserAuth", "addPurchasedProduct"]),
    ...mapActions("analytics", ["trackActivity"]),
    ...mapActions("products", ["hasActivePurchase", "purchaseIap"]),

    retry() {
      if (this.product.id === SETUP_INTENT_PRODUCT_ID) {
        this.$router.removeHash()
      } else {
        this.errorMessage = ""
        this.goToStage(STAGES.PAY)
      }
    },

    handleModalOpen() {
      if (this.stage !== STAGES.CALLBACK) {
        this.track(ACTIVITY_TYPES.CHECKOUT_STARTED)
      }

      if (this.isHandlingAuthComplete) return

      if (this.isAuthenticated) {
        if (
          this.stage !== STAGES.CALLBACK &&
          this.product.id !== SETUP_INTENT_PRODUCT_ID
        ) {
          this.goToStage(STAGES.PAY)
        }
      } else if (!this.hasCheckedAuthentication) {
        setTimeout(() => {
          this.handleModalOpen()
        }, 500)
      } else {
        setTimeout(() => {
          this.$router.push("#auth")
          EventBus.$once(EVENTS.POST_AUTH_FLOW, this.handleAuthComplete)
          EventBus.$once(EVENTS.AUTH_ABORTED, () => {
            EventBus.$off(EVENTS.POST_AUTH_FLOW, this.handleAuthComplete)
          })
        }, 250)
      }
    },

    handleAuthComplete() {
      this.isHandlingAuthComplete = true

      setTimeout(() => {
        if (this.hasPastDueSubscriptionForProduct(this.product.id)) {
          this.$router.removeHash()
          this.handleModalClose(false)

          setTimeout(() => {
            EventBus.$emit(EVENTS.TRIGGER_PAST_DUE_PROMPT)
          }, 350)

          return
        }

        this.goToStage(STAGES.START)

        this.hasActivePurchase(this.product.id).then(hasPurchase => {
          if (hasPurchase) {
            this.$router.removeHash()
            this.handleModalClose(false)
          } else {
            this.goToStage(STAGES.PAY)
          }

          this.isHandlingAuthComplete = false
        })
      }, 100)
    },

    setAuthentication(user) {
      this.paymentUser = user
      this.goToStage(STAGES.PAY)
    },

    handlePurchaseSuccess() {
      this.addPurchasedProduct(this.product.id)
      EventBus.$emit(EVENTS.CHECKOUT_COMPLETED, this.product.id)
      this.$router.removeHash()
      this.$emit("success")
    },

    handleIapPurchaseSuccess(data) {
      this.iapError = null
      this.iapIsLoading = false
      this.iapIsValidating = true

      this.purchaseIap({
        productId: this.product.id,
        transactionId: data[IAP_PURCHASE_PROPERTIES.TRANSACTION_ID],
        originalTransactionId:
          data[IAP_PURCHASE_PROPERTIES.ORIGINAL_TRANSACTION_ID]
      })
        .then(() => {
          this.iapIsValidating = false
          this.handlePurchaseSuccess()
        })
        .catch(err => {
          this.handlePurchaseError(err.response.data)
        })
    },

    handleIapInvalidProduct() {
      this.iapIsLoading = false
      this.iapIsValidating = false
      this.iapError = IAP_ERRORS.INVALID_PRODUCT
    },

    handlePurchaseError(error) {
      this.error = error
      this.goToStage(STAGES.ERROR)
    },

    goToStage(stage) {
      this.$router.push(stage)
    },

    track(activityType) {
      this.trackActivity({
        activityType,
        data: { product: this.product }
      })
    },

    handleModalClose(trackEvent = true) {
      if (trackEvent) {
        this.track(ACTIVITY_TYPES.CHECKOUT_ABORTED)
      }

      EventBus.$off(EVENTS.POST_AUTH_FLOW, this.handleAuthComplete)
      this.$emit("close")
    },

    promptIap() {
      this.iapProgressStep = 0
      this.iapIsLoading = true

      postMessageToNativeApp(
        buildMessage(MESSAGE_TYPES.IAP_PURCHASE_PROMPT, {
          productId: this.product.id
        })
      )

      setTimeout(() => {
        this.iapProgressStep = 1
      }, 2500)

      setTimeout(() => {
        this.iapProgressStep = 2
      }, 7500)
    }
  },

  computed: {
    ...mapState("responsive", ["isDesktop"]),
    ...mapGetters("auth", ["isAuthenticated", "hasCheckedAuthentication"]),
    ...mapGetters(["app"]),
    ...mapGetters("products", ["hasPastDueSubscriptionForProduct"]),

    shouldUseIAP() {
      return isAppStore() && isIos()
    },

    isLoadingStage() {
      return [STAGES.START, STAGES.PAY_IAP, STAGES.CALLBACK].includes(
        this.stage
      )
    }
  },

  mounted() {
    if (this.shouldUseIAP) {
      setupNativePurchaseEventListeners()

      EventBus.$on(EVENTS.IAP_PURCHASE_CANCELED, this.handleModalClose)
      EventBus.$on(EVENTS.IAP_PURCHASE_SUCCESS, this.handleIapPurchaseSuccess)
      EventBus.$on(EVENTS.IAP_INVALID_PRODUCT, this.handleIapInvalidProduct)
    }
  },

  beforeDestroy() {
    if (this.shouldUseIAP) {
      EventBus.$off(EVENTS.IAP_PURCHASE_CANCELED, this.handleModalClose)
      EventBus.$off(EVENTS.IAP_PURCHASE_SUCCESS, this.handleIapPurchaseSuccess)
      EventBus.$off(EVENTS.IAP_INVALID_PRODUCT, this.handleIapInvalidProduct)
    }
  },

  watch: {
    "$route.hash": {
      immediate: true,
      handler(hash) {
        if (Object.values(STAGES).includes(hash)) {
          this.stage = hash

          if (this.shouldUseIAP && this.stage === STAGES.PAY) {
            this.$router.push(STAGES.PAY_IAP)
          }

          if (this.stage === STAGES.PAY_IAP) {
            this.promptIap()
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.checkout__pay-wrapper {
  position: relative;
  top: $size--8;
}

.checkout-modal__content {
  position: relative;
  top: -$size--4;
  padding: 0 $size--4;
}
</style>
